<template>
  <div class="max-w-5xl mx-auto px-4 sm:px-6 relative min-h-[80vh] flex flex-col pt-16 sm:pt-24 pb-12">
    
    <div class="absolute top-3 right-3 sm:top-6 sm:right-6 z-20">
      <div class="relative flex items-center gap-1.5 px-2 py-1 rounded-full transition-colors hover:bg-gray-50/80">
        <img :src="flagUrl" class="w-4 h-4 rounded-full object-cover opacity-80" alt="flag" />
        <select
          v-model="selectedCountry"
          class="appearance-none bg-transparent text-xs font-bold text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer pr-4 py-0.5 z-10 transition-colors"
        >
          <option value="US">USD</option>
          <option value="Canada">CAD</option>
        </select>
        <svg class="absolute right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-300 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <div class="flex flex-col justify-center items-center gap-6 text-center animate-in fade-in slide-in-from-bottom-4 duration-700">
      <h1 class="text-4xl sm:text-6xl font-extrabold text-gray-900 tracking-tight leading-tight">
        What is your <span class="text-emerald-600">Benchmark Gap?</span>
      </h1>
      <p class="text-gray-500 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
        You pay advisors fees to be smart. You spend hours trading to be active.
        <span class="block mt-3 text-gray-800 font-medium">
          But are you actually beating the <strong class="text-gray-900">"Do Nothing" Benchmark</strong>?
        </span>
      </p>

      <div class="w-full max-w-md mt-8 relative">
          <label class="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 text-left pl-4">
            Total Account Value
          </label>
          <div class="relative group">
            <span class="absolute left-6 top-1/2 transform -translate-y-1/2 text-2xl text-gray-300 font-light group-focus-within:text-emerald-500 transition-colors">$</span>
            <input
                ref="mainInput"
                type="text"
                inputmode="numeric"
                :value="displayValue"
                @input="handleInput"
                @keydown.enter="goToAudit"
                class="block w-full bg-white border-2 border-gray-100 hover:border-gray-200 rounded-2xl py-5 pl-12 pr-4 text-left text-4xl font-extrabold text-gray-900 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all shadow-xl shadow-gray-100 placeholder:text-gray-200"
                placeholder="50,000"
                autofocus
            />
            <div class="absolute right-4 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 text-xs font-bold text-gray-300 bg-gray-50 px-2 py-1 rounded border border-gray-100 pointer-events-none">
                <span>PRESS</span> <span class="font-mono">ENTER ↵</span>
            </div>
          </div>
      </div>
    </div>

    <div class="text-center mt-10 pb-12 sm:pb-0 flex flex-col items-center">
      <div class="flex flex-col sm:flex-row gap-4 w-full sm:w-auto px-4 sm:px-0">
        <button
          @click="goToAudit"
          class="flex-1 sm:flex-none group inline-flex items-center justify-center gap-3 bg-gray-900 text-white font-bold px-8 py-4 rounded-xl shadow-xl hover:bg-black hover:scale-105 active:scale-95 transition-all duration-200"
        >
          <span>Reveal My Gap</span>
          <svg class="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
        </button>

        <button
          @click="goToSample"
          class="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-white text-gray-600 font-bold px-8 py-4 rounded-xl border-2 border-gray-100 hover:border-emerald-200 hover:text-emerald-700 hover:bg-emerald-50 active:scale-95 transition-all duration-200"
        >
          <span>See Sample Report</span>
          <span class="text-xl leading-none">🧪</span>
        </button>
      </div>
      
      <p class="text-[11px] sm:text-xs text-gray-400 mt-8 max-w-sm leading-relaxed opacity-80">
        Benchmarked against standard, low-cost efficient portfolios (Vanguard/Blackrock). 
        <br><span class="text-gray-300">100% Free. No Login. No BS.</span>
      </p>
    </div>

    <div v-if="showEmptyModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/20 backdrop-blur-sm animate-in fade-in duration-200" @click.self="showEmptyModal = false">
        <div class="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-sm border border-gray-100 transform transition-all scale-100">
            <h3 class="text-lg font-bold text-gray-900 mb-2 text-center">Need a number first!</h3>
            <p class="text-gray-500 text-center mb-6">Enter your portfolio balance to see your real gap, or view a demo.</p>
            <div class="flex flex-col gap-3">
                <button @click="showEmptyModal = false; $refs.mainInput.focus()" class="w-full py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-black transition-colors">
                    I'll Enter My Data
                </button>
                <button @click="goToSample" class="w-full py-3 rounded-xl bg-emerald-50 text-emerald-700 font-bold border border-emerald-100 hover:bg-emerald-100 transition-colors">
                    Show Me a Sample 🧪
                </button>
            </div>
        </div>
    </div>

  </div>
</template>

<script>
import { detectUserCountry } from "@/utils/location";

export default {
  name: "HomePage",
  data() {
    return {
      rawValue: null, 
      selectedCountry: "US",
      showEmptyModal: false, 
    };
  },
  computed: {
    displayValue() {
      return this.rawValue ? this.rawValue.toLocaleString() : "";
    },
    flagUrl() {
      return this.selectedCountry === "Canada"
        ? "https://flagcdn.com/ca.svg"
        : "https://flagcdn.com/us.svg";
    },
  },
  methods: {
    handleInput(event) {
      const val = event.target.value.replace(/[^0-9]/g, "");
      this.rawValue = val ? parseInt(val, 10) : 0;
      
      // PERSISTENCE (Back to localStorage)
      if (this.rawValue) {
          localStorage.setItem('saved_home_balance', this.rawValue);
      } else {
          localStorage.removeItem('saved_home_balance');
      }
    },
    goToAudit() {
      if(!this.rawValue) {
        this.showEmptyModal = true;
        return;
      }
      if(this.rawValue) {
        localStorage.setItem('onboarding_balance', this.rawValue);
      }
      localStorage.setItem('selectedRisk', 'Balanced');
      this.$router.push({ name: 'PortfolioForm' }); 
    },
    goToSample() {
        localStorage.removeItem('onboarding_balance');
        localStorage.setItem('run_sample_immediately', 'true');
        this.$router.push({ name: 'PortfolioForm' });
    }
  },
  watch: {
    selectedCountry(val) {
      try { localStorage.setItem('selectedCountry', val); } catch (e) { /* ignore */ }
    }
  },
  mounted() {
    try {
        const savedCountry = localStorage.getItem('selectedCountry');
        if (savedCountry) this.selectedCountry = savedCountry;

        // PERSISTENCE (Back to localStorage)
        const savedBal = localStorage.getItem('saved_home_balance');
        if (savedBal && !isNaN(savedBal)) {
            this.rawValue = parseInt(savedBal, 10);
        }
    } catch(e) { /* ignore */ }

    detectUserCountry().then(detected => {
        if (detected && detected !== this.selectedCountry) {
            this.selectedCountry = detected; 
        }
    }).catch(() => { /* ignore */ });
  },
};
</script>